import type { TieredSubscriptionProduct } from '@vy/services';
import {
  CommercialTermsPriceEstimator,
  FixedOneTimePriceEstimator,
  getBillingIntervalInMonths,
  isCommercialTermsProduct,
  isCommissionProduct,
  isFixedProduct,
  isOneTimeProduct,
  isSubscriptionProduct,
  isUsageProduct,
  isRefundProduct,
  RefundPriceEstimator,
  isFixedDeductionProductOnly,
  isPercentageDeductionProductOnly,
  SubscriptionPriceEstimator,
  isTieredSubscriptionUsageProductOnly,
} from '@vy/services';
import type { UsageProduct } from '@vy/types';
import {
  UNLIMITED_DURATION,
  type BillingInterval,
  type FixedProductInput,
  type PlanBillingData,
  type PlanInputFormFixedProduct,
  type PlanInputFormUsageProduct,
  type UsageProductInput,
} from '@vy/types';
import { isNotNullOrUndefined, isNullOrUndefined, raise, safeDivision, sumBy, vySerialize } from '@vy/utils';
import { DEFAULT_PLAN_DURATION } from '../../../create-plan-page-consts/create-plan-default-values';

export function getProductInputEstimation(
  product: PlanInputFormFixedProduct | PlanInputFormUsageProduct,
  billingData: Partial<PlanBillingData>,
) {
  const validBillingData = buildValidBillingData(billingData);

  if (isFixedProduct<FixedProductInput>(product)) {
    return getFixedProductEstimation(product, validBillingData);
  }

  if (isNullOrUndefined(validBillingData.billingInterval)) {
    return 0;
  }

  if (isUsageProduct<UsageProductInput>(product)) {
    return getUsageProductInputEstimation(product, validBillingData.billingInterval);
  }

  return raise(`Unknown product type: ${vySerialize(product, null, 2)}`);
}

export function getFixedProductEstimation(product: FixedProductInput, billingData: Partial<PlanBillingData>) {
  if (isOneTimeProduct(product)) {
    const price = product.variants[0].pricing.priceConfiguration?.price;

    return isNotNullOrUndefined(price) && !isNaN(price)
      ? new FixedOneTimePriceEstimator().estimate(product, undefined)
      : 0;
  }

  if (isRefundProduct(product)) {
    const price = product.variants[0].pricing.priceConfiguration?.price;

    return isNotNullOrUndefined(price) && !isNaN(price)
      ? new RefundPriceEstimator().estimate(product, undefined)
      : 0;
  }

  if (isSubscriptionProduct(product)) {
    return new SubscriptionPriceEstimator().estimate(product, billingData);
  }

  if (isCommissionProduct(product)) {
    return 0;
  }

  if (isCommercialTermsProduct(product)) {
    const price = product.variants[0].pricing.priceConfiguration?.price;

    return isNotNullOrUndefined(price) && !isNaN(price)
      ? new CommercialTermsPriceEstimator().estimate(product, undefined)
      : 0;
  }

  if (isFixedDeductionProductOnly(product)) {
    const price = product.variants[0].pricing.priceConfiguration.amountToDeduct;

    return isNotNullOrUndefined(price) && !isNaN(price) ? price : 0;
  }

  if (isPercentageDeductionProductOnly(product)) {
    return 0;
  }

  return raise(`Unsupported product type ${product}`);
}

export function getUsageProductInputEstimation(
  product: UsageProductInput | UsageProduct,
  billingPeriod: BillingInterval,
) {
  const billingPeriodInMonths = getBillingIntervalInMonths(billingPeriod);

  const baseAmountEstimation = (product.basePrice ?? 0) * (12 / billingPeriodInMonths);

  if (isTieredSubscriptionUsageProductOnly<TieredSubscriptionProduct>(product)) {
    const multiplier = safeDivision(12, product.chargingCadence ?? 1);

    const variantsEstimation = sumBy(
      product.variants,
      (variant) => (variant.pricing.priceConfiguration?.tiers[0].flatRate ?? 0) * multiplier,
    );

    return baseAmountEstimation + variantsEstimation;
  }

  return baseAmountEstimation;
}

export function buildValidBillingData(billingData: Partial<PlanBillingData>): Partial<PlanBillingData> {
  return {
    ...billingData,
    duration: billingData.duration !== UNLIMITED_DURATION ? billingData.duration : DEFAULT_PLAN_DURATION,
  };
}
